<template>
  <div>
    <v-alert type="info" outlined dense border="left" class="mb-3">
      <div class="font-weight-bold">จำลองการคำนวณรายแห่ง — ตรวจสอบการบริหารวงเงิน {{ params.budgetPct }}% ก่อนประมวลผลจริง (ไม่บันทึกผล)</div>
      <div class="caption">
        ฐานวงเงิน ณ 1 ก.ย. {{ compYear }}: วงเงิน = {{ params.budgetPct }}% ของค่าตอบแทนรวมทุกคนในฐาน (รวมผู้เกษียณ/ลาออกก่อน 1 ต.ค.
        ซึ่งไม่ได้รับการเลื่อน) · ผู้ย้ายมีผล 1 ต.ค. คำนวณที่สังกัดเดิม · วิธีร้อยละร่วมตามคะแนน: ทุกคนใช้อัตรากลางเดียวกัน
        ร้อยละ = อัตรากลาง × คะแนน ÷ 100 (ไม่เกิน {{ params.maxPct }}%) ปัดขึ้นหลักสิบทุกคน
        หากวงเงินไม่พอลดอัตรากลางให้ทุกคนเท่ากัน
      </div>
    </v-alert>

    <v-row dense align="center">
      <v-col cols="12" md="4">
        <v-autocomplete v-model="collegeCode" :items="units" item-value="college_code" :item-text="unitText"
          label="สถานศึกษา/หน่วยงาน" outlined dense hide-details :loading="loadingUnits" clearable />
      </v-col>
      <v-col cols="6" md="2">
        <v-select v-model="params.roundMode" :items="roundItems" label="รอบผลประเมิน" outlined dense hide-details />
      </v-col>
      <v-col cols="6" md="2">
        <v-select v-model="salarySource" :items="salaryItems" label="ฐานค่าตอบแทน" outlined dense hide-details />
      </v-col>
      <v-col cols="6" md="1">
        <v-text-field v-model.number="params.minScore" type="number" label="คะแนนขั้นต่ำ" outlined dense hide-details />
      </v-col>
      <v-col cols="6" md="1">
        <v-text-field v-model.number="params.minMonths" type="number" label="เดือนขั้นต่ำ" outlined dense hide-details />
      </v-col>
      <v-col cols="12" md="2">
        <v-btn block color="primary" :loading="loading" :disabled="!collegeCode" @click="simulate">
          <v-icon left>mdi-calculator-variant</v-icon>จำลอง
        </v-btn>
      </v-col>
    </v-row>

    <template v-if="result">
      <div class="subtitle-1 font-weight-bold mt-4">{{ collegeName }} ({{ collegeCode }})</div>

      <!-- ที่มาของวงเงิน -->
      <v-row dense class="mt-2">
        <v-col cols="12" md="6">
          <v-card outlined class="pa-3">
            <div class="subtitle-2 font-weight-bold mb-2">1. ที่มาของวงเงิน</div>
            <table class="explain-table">
              <tr><td>ผู้อยู่ในฐานวงเงิน ณ 1 ก.ย.</td><td>{{ fmt(s.personnel_count) }} คน</td></tr>
              <tr><td class="pl-4">— ปฏิบัติงานต่อ (พิจารณาเลื่อน)</td><td>{{ fmt(s.personnel_count - s.retired_count - s.resigned_count) }} คน</td></tr>
              <tr v-if="s.retired_count"><td class="pl-4">— ครบเกษียณ 30 ก.ย. (นับเป็นวงเงิน ไม่ได้เลื่อน)</td><td>{{ fmt(s.retired_count) }} คน</td></tr>
              <tr v-if="s.resigned_count"><td class="pl-4">— ลาออก 1–30 ก.ย. (นับเป็นวงเงิน ไม่ได้เลื่อน)</td><td>{{ fmt(s.resigned_count) }} คน</td></tr>
              <tr v-if="s.transfer_count"><td class="pl-4">— ย้ายมีผล 1 ต.ค. (คำนวณที่สังกัดนี้)</td><td>{{ fmt(s.transfer_count) }} คน</td></tr>
              <tr><td>ค่าตอบแทนรวม</td><td>{{ fmt(s.total_salary) }} บาท/เดือน</td></tr>
              <tr class="font-weight-bold"><td>วงเงิน {{ params.budgetPct }}%</td><td>{{ fmt2(s.pool_month) }} บาท/เดือน ({{ fmt(s.budget_year) }} บาท/ปี)</td></tr>
            </table>
          </v-card>
        </v-col>
        <v-col cols="12" md="6">
          <v-card outlined class="pa-3">
            <div class="subtitle-2 font-weight-bold mb-2">2. ผลการบริหารวงเงิน (ร้อยละร่วมตามคะแนน)</div>
            <table class="explain-table">
              <tr><td>ผู้มีสิทธิ์ได้รับการเลื่อน</td><td>{{ fmt(s.eligible_count) }} คน</td></tr>
              <tr class="font-weight-bold"><td>อัตรากลาง (ร้อยละเมื่อคะแนน 100)</td><td>{{ s.factor_k ? s.factor_k.toFixed(4) + "%" : "-" }}</td></tr>
              <tr><td>ใช้วงเงิน</td><td>{{ fmt(s.distributed_month) }} บาท/เดือน ({{ usePct.toFixed(2) }}%)</td></tr>
              <tr><td>คงเหลือ</td><td>{{ fmt2(s.leftover_month) }} บาท/เดือน</td></tr>
              <tr v-if="s.pct_capped_count"><td>ได้เต็มเพดาน {{ params.maxPct }}%</td><td>{{ fmt(s.pct_capped_count) }} คน</td></tr>
              <tr v-if="s.capped_count"><td>ถึงขั้นสูงกลุ่มงาน</td><td>{{ fmt(s.capped_count) }} คน</td></tr>
            </table>
            <v-alert v-if="s.leftover_reason" dense outlined type="info" class="mt-2 mb-0 caption">
              <strong>เหตุผลที่มีเงินเหลือ:</strong> {{ s.leftover_reason }}
            </v-alert>
          </v-card>
        </v-col>
      </v-row>

      <v-alert v-if="s.notes && s.notes.length" dense outlined type="warning" class="mt-2">
        <div class="font-weight-bold">ข้อสังเกตประกอบการพิจารณา</div>
        <ul class="caption mb-0"><li v-for="(n, i) in s.notes" :key="i">{{ n }}</li></ul>
      </v-alert>

      <v-card outlined class="pa-3 mt-2">
        <div class="subtitle-2 font-weight-bold mb-1">3. เปรียบเทียบกับสูตรเดิมของสถานศึกษา</div>
        <div class="caption">
          สูตรเดิม: ใช้วงเงิน {{ fmt(legacy.summary.distributed_month) }} บาท/เดือน
          · ร้อยละร่วมตามคะแนน: {{ fmt(s.distributed_month) }} บาท/เดือน
          · จำนวนเงินต่างจากสูตรเดิม {{ fmt(diffCount) }} คน
          <span v-if="legacyCut"> · สูตรเดิมมีการตัดเงินรายบุคคลทีละ 10 บาท/ปัดเพื่อคุมวงเงิน ทำให้ผู้มีคะแนนเท่ากันได้ร้อยละไม่เท่ากัน</span>
        </div>
      </v-card>

      <!-- รายบุคคล -->
      <div class="d-flex align-center mt-4 mb-1">
        <div class="subtitle-2 font-weight-bold">4. รายละเอียดรายบุคคลและเหตุผลประกอบ</div>
        <v-spacer />
        <v-btn-toggle v-model="filter" dense mandatory class="mr-2">
          <v-btn x-small value="all">ทั้งหมด ({{ records.length }})</v-btn>
          <v-btn x-small value="eligible">ได้เลื่อน ({{ s.eligible_count }})</v-btn>
          <v-btn x-small value="ineligible">ไม่ได้เลื่อน ({{ s.ineligible_count }})</v-btn>
          <v-btn x-small value="diff">ต่างจากสูตรเดิม ({{ diffCount }})</v-btn>
        </v-btn-toggle>
        <v-btn small color="success" @click="exportXlsx"><v-icon small left>mdi-microsoft-excel</v-icon>xlsx</v-btn>
      </div>
      <v-data-table :headers="headers" :items="filtered" dense show-expand single-expand item-key="id_card"
        :items-per-page="50" :footer-props="{ itemsPerPageOptions: [50, 100, -1] }" class="sim-table">
        <template v-slot:item.full_name="{ item }">
          <div class="font-weight-medium">{{ item.full_name }}</div>
          <div class="caption grey--text">{{ item.id_card }} · {{ item.name_postion }}</div>
          <v-chip v-if="item.base_status === 'retired'" x-small color="blue-grey" dark>เกษียณ</v-chip>
          <v-chip v-if="item.base_status === 'resigned'" x-small color="blue-grey" dark>ลาออก</v-chip>
          <v-chip v-if="item.transfer_note" x-small color="purple" dark>ย้าย 1 ต.ค.</v-chip>
        </template>
        <template v-slot:item.salary_s="{ item }">{{ fmt(item.salary_s) }}</template>
        <template v-slot:item.performance_score="{ item }">
          <span v-if="item.performance_score > 0">{{ item.performance_score }}<div class="caption grey--text" style="white-space:nowrap">{{ scoreSourceText(item) }}</div></span><span v-else class="grey--text">-</span>
          <div v-if="item.assessment_ref" class="caption grey--text">
            ร.{{ item.score_round }} · {{ statusText(item.assessment_status) }}
          </div>
        </template>
        <template v-slot:item.pre="{ item }">
          <template v-if="item.eligible">
            <div>{{ preMap[item.id_card].pct.toFixed(3) }}% · {{ fmt(preMap[item.id_card].amount) }}</div>
            <div class="caption grey--text">ใหม่ {{ fmt(preMap[item.id_card].newSalary) }}</div>
          </template>
          <span v-else class="grey--text">-</span>
        </template>
        <template v-slot:item.actual_pct_raw="{ item }">{{ item.eligible ? item.actual_pct_raw.toFixed(3) + "%" : "-" }}</template>
        <template v-slot:item.final_rounded="{ item }">
          <span class="font-weight-bold primary--text">{{ item.eligible ? fmt(item.final_rounded) : "-" }}</span>
          <div v-if="item.eligible" class="caption grey--text" style="white-space:nowrap">ใหม่ {{ fmt(item.new_salary) }}
            <span v-if="item.final_rounded !== preMap[item.id_card].amount" class="error--text">({{ diffText(item) }})</span>
          </div>
          <div v-if="item.pct_capped" class="caption warning--text">เพดาน {{ params.maxPct }}%</div>
          <div v-if="item.capped" class="caption warning--text">ขั้นสูงกลุ่มงาน</div>
        </template>
        <template v-slot:item.legacy="{ item }">
          <span :class="legacyMap[item.id_card] !== item.final_rounded ? 'error--text' : 'grey--text'">
            {{ fmt(legacyMap[item.id_card]) }}
          </span>
        </template>
        <template v-slot:item.explain="{ item }">
          <div class="caption" :class="item.eligible ? '' : 'error--text'" style="white-space:normal">{{ item.explain }}</div>
          <div v-if="item.transfer_note" class="caption purple--text">{{ item.transfer_note }}</div>
          <div v-if="item.salary_note" class="caption grey--text">{{ item.salary_note }}</div>
        </template>
        <template v-slot:expanded-item="{ headers: h, item }">
          <td :colspan="h.length" class="py-2 grey lighten-5">
            <v-row dense>
              <v-col cols="12" md="7">
                <div class="caption font-weight-bold mb-1">ตรวจคุณสมบัติรายข้อ</div>
                <div v-for="c in item.checks" :key="c.key" class="caption">
                  <v-icon x-small :color="c.pass ? 'success' : c.blocking ? 'error' : 'warning'">
                    {{ c.pass ? "mdi-check-circle" : c.blocking ? "mdi-close-circle" : "mdi-alert" }}
                  </v-icon>
                  {{ c.label }} — {{ c.detail }}<span v-if="!c.blocking && !c.pass"> (ข้อสังเกต ไม่ตัดสิทธิ์)</span>
                </div>
              </v-col>
              <v-col cols="12" md="5">
                <div class="caption font-weight-bold mb-1">ที่มาของผลการประเมิน</div>
                <div v-if="item.assessment_ref" class="caption">
                  <div class="font-weight-bold">ฐานคะแนน: {{ scoreSourceText(item) }}</div>
                  <div v-for="x in item.assessment_ref.rounds || []" :key="x.round" class="mt-1">
                    รอบ {{ x.round }} ปีงบ {{ compYear }} · เลขที่รายการ #{{ x.id }} · {{ x.score }} คะแนน<br />
                    ประเมินโดยหน่วยงาน {{ x.college }}
                    <span v-if="x.college !== collegeCode" class="purple--text">(สถานศึกษาเดิม)</span><br />
                    ผู้ประเมิน {{ x.assessor || "-" }} · ผู้อนุมัติ {{ x.approver || "-" }}
                    · วันที่อนุมัติ {{ x.approved || "-" }}<br />
                    เอกสารแนบ (ปผ.พนร.) {{ x.files || 0 }} ไฟล์ · สถานะ {{ statusText(x.status) }}
                  </div>
                </div>
                <div v-else class="caption error--text">ไม่พบผลการประเมินในระบบ</div>
              </v-col>
            </v-row>
          </td>
        </template>
      </v-data-table>
    </template>
  </div>
</template>

<script>
// จำลองการคำนวณการเลื่อนค่าตอบแทนรายแห่ง (ส่วนกลาง) — ใช้ข้อมูลฐานคำนวณ ณ 30 ก.ย. (govem_comp_roster.php)
// คำนวณฝั่งหน้าเว็บด้วย govemCompensationEngine.js ทั้งวิธีใหม่และสูตรเดิมเพื่อเปรียบเทียบ ไม่บันทึกผล
import * as XLSX from "xlsx";
import { calculateCollegeV2, calculateCollege, scoreSourceText, preManagement } from "@/utils/govemCompensationEngine";
import { compareSheet, pctOf } from "@/utils/govemCompOrderSheet";

const API = "govem_compensation_central.php";
const STATUS = { approved: "อนุมัติ", submitted: "ส่งแล้ว", draft: "ร่าง", rejected: "ส่งกลับ" };

export default {
  name: "CompensationSimulator",
  props: {
    compYear: { type: Number, required: true },
    defaultRoundMode: { type: String, default: "average" },
    defaultSalarySource: { type: String, default: "pt" },
    initialCollege: { type: String, default: null }
  },
  data() {
    return {
      units: [],
      loadingUnits: false,
      loading: false,
      collegeCode: this.initialCollege,
      collegeName: "",
      salarySource: this.defaultSalarySource,
      params: { budgetPct: 4, maxPct: 6, roundMode: this.defaultRoundMode, minScore: 75, minMonths: 8 },
      result: null,
      legacy: null,
      positions: {},
      filter: "all",
      roundItems: [
        { text: "เฉลี่ย 2 รอบ (ระเบียบ คพร.)", value: "average" },
        { text: "รอบล่าสุด (เปรียบเทียบ)", value: "latest" },
        { text: "รอบที่ 1 (เปรียบเทียบ)", value: "1" },
        { text: "รอบที่ 2 (เปรียบเทียบ)", value: "2" }
      ],
      salaryItems: [
        { text: "ทะเบียนประวัติ", value: "pt" },
        { text: "กรอบอัตรากำลัง", value: "ge" }
      ],
      headers: [
        { text: "ชื่อ-สกุล", value: "full_name" },
        { text: "ค่าตอบแทน ณ 1 ก.ย.", value: "salary_s", align: "right" },
        { text: "ฐานคะแนน (เฉลี่ย 2 รอบ)", value: "performance_score", align: "center" },
        { text: "ก่อนบริหารวงเงิน (ร้อยละตามผลประเมิน · บาท/ด.)", value: "pre", align: "right", sortable: false },
        { text: "หลังบริหาร ร้อยละ", value: "actual_pct_raw", align: "center" },
        { text: "หลังบริหาร เลื่อน (บาท/ด.)", value: "final_rounded", align: "right" },
        { text: "สูตรเดิม", value: "legacy", align: "right", sortable: false },
        { text: "เหตุผลประกอบการคำนวณ", value: "explain", sortable: false },
        { text: "", value: "data-table-expand" }
      ]
    };
  },
  computed: {
    s() {
      return this.result ? this.result.summary : {};
    },
    records() {
      return this.result ? this.result.records : [];
    },
    usePct() {
      return this.s.pool_month ? (this.s.distributed_month / this.s.pool_month) * 100 : 0;
    },
    // ก่อนบริหารวงเงิน = ร้อยละตามผลประเมินล้วน (เพดาน × คะแนน ÷ 100) ไม่คำนึงวงเงิน
    preMap() {
      const m = {};
      this.records.forEach(r => (m[r.id_card] = preManagement(r.salary_s, r.performance_score, r.eligible, this.params.maxPct, r.name_type_group)));
      return m;
    },
    legacyMap() {
      const m = {};
      if (this.legacy) this.legacy.records.forEach(r => (m[r.id_card] = r.final_rounded));
      return m;
    },
    diffCount() {
      return this.records.filter(r => this.legacyMap[r.id_card] !== r.final_rounded).length;
    },
    legacyCut() {
      // สูตรเดิมตัดเงินรายบุคคล → คนคะแนนเท่ากันได้ร้อยละต่างกัน
      if (!this.legacy) return false;
      const byScore = {};
      this.legacy.records.filter(r => r.eligible && !r.capped).forEach(r => {
        const k = r.performance_score;
        (byScore[k] = byScore[k] || new Set()).add((r.final_rounded / r.salary_s).toFixed(3));
      });
      return Object.values(byScore).some(set => set.size > 1);
    },
    filtered() {
      if (this.filter === "eligible") return this.records.filter(r => r.eligible);
      if (this.filter === "ineligible") return this.records.filter(r => !r.eligible);
      if (this.filter === "diff") return this.records.filter(r => this.legacyMap[r.id_card] !== r.final_rounded);
      return this.records;
    }
  },
  watch: {
    compYear() {
      this.loadUnits();
    }
  },
  mounted() {
    this.loadUnits();
    if (this.collegeCode) this.simulate();
  },
  methods: {
    scoreSourceText,
    fmt(v) {
      return v === undefined || v === null ? "-" : Number(v).toLocaleString("th-TH", { maximumFractionDigits: 0 });
    },
    fmt2(v) {
      return Number(v || 0).toLocaleString("th-TH", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    diffText(item) {
      const d = item.final_rounded - this.preMap[item.id_card].amount;
      return (d > 0 ? "+" : "") + this.fmt(d) + " จากก่อนบริหาร";
    },
    statusText(s) {
      return STATUS[s] || s || "-";
    },
    unitText(u) {
      return `${u.college_name} (${u.college_code})${u.province_name ? " · " + u.province_name : ""}`;
    },
    async post(payload) {
      const res = await this.$http.post(API, { ApiKey: "HRvec2021", ...payload });
      if (!res.data.status) throw new Error(res.data.message);
      return res.data;
    },
    async loadUnits() {
      this.loadingUnits = true;
      try {
        this.units = (await this.post({ action: "unit_list", comp_year: this.compYear })).units;
      } catch (e) {
        this.units = [];
      } finally {
        this.loadingUnits = false;
      }
    },
    async simulate() {
      this.loading = true;
      try {
        const d = await this.post({ action: "simulate_data", comp_year: this.compYear, college_code: this.collegeCode,
          salary_source: this.salarySource });
        this.collegeName = d.college_name;
        this.positions = {};
        d.employees.forEach(e => (this.positions[String(e.id_card)] = e.id_position));
        const params = { ...this.params, compYear: this.compYear, holidays: d.holidays || [] };
        this.result = calculateCollegeV2(d.employees, params);
        this.legacy = calculateCollege(d.employees, params);
      } catch (e) {
        this.$emit("error", e.message);
      } finally {
        this.loading = false;
      }
    },
    exportXlsx() {
      const s = this.s;
      const head = [
        [`จำลองการคำนวณการเลื่อนค่าตอบแทน ${this.collegeName} (${this.collegeCode}) ปีงบประมาณ ${this.compYear}`],
        [`วงเงิน ${this.params.budgetPct}% = ${this.fmt2(s.pool_month)} บาท/เดือน · อัตรากลาง ${s.factor_k}% · ใช้ ${this.fmt(s.distributed_month)} · คงเหลือ ${this.fmt2(s.leftover_month)}`],
        [`เหตุผลเงินเหลือ: ${s.leftover_reason || "-"}`],
        ...(s.notes || []).map(n => [`ข้อสังเกต: ${n}`]),
        []
      ];
      const rows = this.records.map((r, i) => [i + 1, r.id_card, r.full_name, r.name_postion,
        r.base_status === "retired" ? "เกษียณ" : r.base_status === "resigned" ? "ลาออก" : "ปฏิบัติงาน", r.transfer_note || "",
        r.salary_s, r.score_r1 || "", r.score_r2 || "", r.performance_score, r.score_round || "", this.statusText(r.assessment_status),
        r.assessment_ref ? (r.assessment_ref.rounds || []).map(x => `ร.${x.round} #${x.id} (${x.college})`).join(", ") : "", r.eligible ? "มีสิทธิ์" : "ไม่มีสิทธิ์",
        r.eligible ? this.preMap[r.id_card].pct : "", r.eligible ? this.preMap[r.id_card].amount : "",
        r.eligible ? r.actual_pct_raw : "", r.final_rounded, this.legacyMap[r.id_card], r.explain]);
      const ws = XLSX.utils.aoa_to_sheet([...head, ["ลำดับ", "เลขบัตร", "ชื่อ-สกุล", "ตำแหน่ง", "สถานะ", "การย้าย", "ค่าตอบแทน ณ 1 ก.ย.",
        "รอบ 1", "รอบ 2", "ฐานคะแนน (เฉลี่ย)", "รอบที่ใช้", "สถานะผลประเมิน", "ที่มาผลประเมิน", "สิทธิ์", "ก่อนบริหาร ร้อยละ", "ก่อนบริหาร เลื่อน (บาท)", "หลังบริหาร ร้อยละ", "หลังบริหาร เลื่อน (บาท)", "สูตรเดิม (บาท)", "เหตุผลประกอบ"], ...rows]);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "จำลองการคำนวณ");
      const rows2 = this.records.map(r => ({
        full_name: r.full_name,
        id_position: this.positions[r.id_card],
        name_postion: r.name_postion,
        name_type_group: r.name_type_group,
        score: r.performance_score,
        salary: r.salary_s,
        pre: this.preMap[r.id_card],
        post: { pct: pctOf(r.final_rounded, r.salary_s), amount: r.final_rounded, newSalary: r.salary_s + r.final_rounded },
        note: [r.eligible ? "" : r.ineligible_reason, r.transfer_note].filter(Boolean).join(" · ")
      }));
      XLSX.utils.book_append_sheet(wb, compareSheet([{ name: `${this.collegeName} (${this.collegeCode})`, rows: rows2 }], {
        titleRows: [`จำลองการคำนวณ — เทียบก่อน-หลังบริหารวงเงิน ปีงบประมาณ ${this.compYear} (ไม่ใช่ผลจริง)`,
          `ก่อนบริหารวงเงิน = ร้อยละตามผลประเมินล้วน (${this.params.maxPct}% × คะแนน ÷ 100) ปัดขึ้นหลักสิบ · หลังบริหารวงเงิน = อัตรากลาง ${(+s.factor_k || 0).toFixed(4)}% ภายในวงเงิน ${this.params.budgetPct}%`]
      }), "เทียบก่อน-หลังบริหารวงเงิน");
      XLSX.writeFile(wb, `จำลองเลื่อนค่าตอบแทน_${this.collegeCode}_${this.compYear}.xlsx`);
    }
  }
};
</script>

<style scoped>
.explain-table {
  width: 100%;
  font-size: 13px;
}
.explain-table td:last-child {
  text-align: right;
  white-space: nowrap;
}
.sim-table >>> td {
  vertical-align: top;
  padding-top: 4px !important;
  padding-bottom: 4px !important;
}
</style>
